import { 
  collection, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const productsCollection = 'products';

// Get all products
export const getProducts = async () => {
  const productsRef = collection(db, productsCollection);
  const snapshot = await getDocs(productsRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get products filtered by location
export const getProductsByLocation = async (location) => {
  const productsRef = collection(db, productsCollection);
  const q = query(productsRef, where("location", "==", location));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get all products and filter by location (for cases where location might not be indexed)
export const getAllProductsFilteredByLocation = async (location) => {
  const products = await getProducts();
  
  // Define location inventory mappings (fallback for older products without location field)
  const locationInventory = {
    'Angola': [
      'Beach Oasis',
      'Mini Mouse',
      'Toy Story', 
      'SunShine WaterSlide',
      'Dino',
      'America',
      'White Folding Tables',
      'White Folding Chairs'
    ],
    'Elkhart': [
      'Blue Marble Combo',
      'Carnival Combo',
      'XL Tropical Dual Lane Combo',
      'RAINBOW DUAL LANE COMBO',
      'Jellybean Dual Lane Combo'
    ]
  };
  
  return products.filter(product => {
    if (product.location) {
      return product.location === location || product.location === 'Both';
    }
    
    const inventory = locationInventory[location] || [];
    return inventory.some(item => 
      item.toLowerCase().includes(product.name.toLowerCase()) ||
      product.name.toLowerCase().includes(item.toLowerCase())
    );
  });
};

// Get a single product by ID
export const getProductById = async (id) => {
  const productRef = doc(db, productsCollection, id);
  const productDoc = await getDoc(productRef);
  
  if (productDoc.exists()) {
    return {
      id: productDoc.id,
      ...productDoc.data()
    };
  } else {
    return null;
  }
};

// Add a new product
export const addProduct = async (productData) => {
  const productsRef = collection(db, productsCollection);
  const newProduct = {
    ...productData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  const docRef = await addDoc(productsRef, newProduct);
  return {
    id: docRef.id,
    ...newProduct
  };
};

// Update an existing product
export const updateProduct = async (id, productData) => {
  const productRef = doc(db, productsCollection, id);
  const updatedData = {
    ...productData,
    updatedAt: serverTimestamp()
  };
  
  await updateDoc(productRef, updatedData);
  return {
    id,
    ...updatedData
  };
};

// Delete a product (and clean up any associated Storage images)
export const deleteProduct = async (id) => {
  const productRef = doc(db, productsCollection, id);

  // Read the product first so we can clean up its images afterwards.
  // We tolerate a missing doc here — the deleteDoc below will be a no-op
  // and we still want to return successfully.
  let productData = null;
  try {
    const snap = await getDoc(productRef);
    if (snap.exists()) productData = snap.data();
  } catch (err) {
    // Don't block the delete on a read failure — log and continue.
    console.error('Error reading product before delete:', err);
  }

  // Delete the Firestore doc first. If this fails (e.g. permission-denied),
  // we want the error to bubble up to the UI rather than silently orphan files.
  await deleteDoc(productRef);

  // Best-effort Storage cleanup. Failures here are logged but don't fail
  // the operation — the product is already gone from the catalog.
  if (productData) {
    const storage = getStorage();
    const pathsToDelete = new Set();

    // New shape: images: [{ url, storagePath?, path? }]
    if (Array.isArray(productData.images)) {
      for (const img of productData.images) {
        const p = img?.storagePath || img?.path;
        if (p) pathsToDelete.add(p);
      }
    }
    // Legacy shape: single imagePath / imageUrl
    if (productData.imagePath) pathsToDelete.add(productData.imagePath);

    for (const path of pathsToDelete) {
      try {
        await deleteObject(ref(storage, path));
      } catch (err) {
        // Common case: file already missing (storage/object-not-found).
        // Anything else is worth surfacing in the console for debugging.
        console.error(`Error deleting product image at "${path}":`, err);
      }
    }
  }

  return id;
};

// Upload product image to Firebase Storage
export const uploadProductImage = async (file, productId) => {
  const storage = getStorage();
  const fileName = `products/${productId}/${file.name}`;
  const storageRef = ref(storage, fileName);
  
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);
  
  return {
    url: downloadURL,
    path: fileName
  };
};

// Delete product image from Firebase Storage
export const deleteProductImage = async (imagePath) => {
  if (!imagePath) return;
  
  const storage = getStorage();
  const imageRef = ref(storage, imagePath);
  
  try {
    await deleteObject(imageRef);
  } catch (error) {
    console.error('Error deleting image:', error);
  }
};

// Filter products by category
export const getProductsByCategory = async (categoryId) => {
  const productsRef = collection(db, productsCollection);
  const q = query(productsRef, where("categoryId", "==", categoryId));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Search products by name and description
export const searchProducts = async (searchTerm) => {
  // Firebase doesn't support full-text search, so we'll get all products and filter
  const products = await getProducts();
  
  return products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

// Get low stock products
export const getLowStockProducts = async (threshold = 5) => {
  const productsRef = collection(db, productsCollection);
  const q = query(productsRef, where("stock", "<=", threshold));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};
